// Server-side user preferences. One row per user in the `user_preferences`
// collection, carrying a JSON `data` blob that any page can read and write
// through this hook. Falls back to localStorage when the collection is
// absent (e.g. before the migration runs) so the page still works.
//
// The server row is scoped by a Directus permission filter (`user._eq
// $CURRENT_USER`), so a user can only ever read or write their own row —
// the hook does not need to pass a user id.
//
// There is exactly one store for the whole app, held by PreferencesProvider.
// That is load-bearing, not tidiness: the row carries a single `data` blob and
// a write sends the whole blob, so two components each holding their own copy
// would each PATCH their own snapshot and the later write would drop the
// earlier one's key. Sharing one copy is what makes `tasks_layout` and
// `shortcuts` survive each other.
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { directus } from "@/providers/directus";
import { readMe, readItems, createItem, updateItem } from "../providers/api.ts";
import { mergePref, hasAuthToken as parseAuthToken, type PrefsData } from "@/lib/preferences";
import { roleCanSee } from "@/providers/accessControl";
import {
  registerFavoritesPersister,
  syncFavoritesFromServer,
  clearFavoritesCache,
  loadFavorites,
} from "@/lib/favorites";

const LS_FALLBACK_KEY = "cordite_prefs";

type PrefsContextValue = {
  prefs: PrefsData;
  setPref: (key: string, value: unknown) => void;
  loading: boolean;
};

const PreferencesContext = createContext<PrefsContextValue | null>(null);

/** True once a login has put an access token in storage. */
function hasAuthToken(): boolean {
  try {
    return parseAuthToken(localStorage.getItem("cordite_auth"));
  } catch {
    return false;
  }
}

function readFallback(): PrefsData {
  try {
    const raw = localStorage.getItem(LS_FALLBACK_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [prefs, setPrefs] = useState<PrefsData>({});
  const [loading, setLoading] = useState(true);
  const rowIdRef = useRef<string | null>(null);
  // The blob as last known, kept outside React state so a write can merge into
  // it without waiting for a render.
  const prefsRef = useRef<PrefsData>({});
  // Writes are chained rather than fired in parallel: each PATCH sends the full
  // blob, so overlapping requests could otherwise land out of order.
  const writeChainRef = useRef<Promise<unknown>>(Promise.resolve());
  // The load, shared rather than repeated. Holding the promise (not a boolean)
  // is what makes this safe under StrictMode's mount/unmount/remount: the
  // second invocation joins the first request instead of either firing a
  // duplicate or, worse, being turned away by a flag while the first result is
  // discarded as stale.
  const loadRef = useRef<Promise<PrefsData> | null>(null);

  // Only the presence of a token matters here, but it can appear at any time
  // (login) and the provider sits above the router, so it never remounts.
  // Subscribing to the location is what re-renders this provider on the
  // post-login redirect, which is the moment `authed` flips. The value itself
  // is deliberately not an effect dependency — see below.
  useLocation();
  const authed = hasAuthToken();

  const setPref = useCallback((key: string, value: unknown) => {
    // Merged onto the ref, which is the newest blob the app has seen — not onto
    // a copy captured at render time. See lib/preferences.ts.
    const next = mergePref(prefsRef.current, key, value);
    prefsRef.current = next;
    setPrefs(next);

    const id = rowIdRef.current;
    if (!id) {
      try {
        localStorage.setItem(LS_FALLBACK_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return;
    }
    writeChainRef.current = writeChainRef.current
      .then(() =>
        directus.request(updateItem("user_preferences", id, { data: prefsRef.current } as any)),
      )
      .catch(() => {
        try {
          localStorage.setItem(LS_FALLBACK_KEY, JSON.stringify(prefsRef.current));
        } catch {
          /* ignore */
        }
      });
  }, []);

  useEffect(() => {
    // Asking for the row before login gets a 403 that is indistinguishable from
    // "the collection was never migrated", which used to drop the whole app
    // into the localStorage fallback for the rest of the session.
    if (!authed) return;
    // A client holds no grant on `user_preferences` and never will: the
    // collection is staff bookkeeping (layouts, key bindings, dock side) and
    // the portal has no surface that writes one. Asking anyway costs a 403 on
    // every portal page load, which lands in the catch below and works, but it
    // is a refusal in the network log of a product whose whole boundary story
    // is "a client's session makes no request it is not allowed to make" --
    // and `web/e2e/305-portal-client-identity-local.mjs` fails a run for it,
    // correctly. `roleCanSee` is the same declaration the nav and the access
    // provider read, so this cannot drift from what the server grants
    // independently of them.
    if (!roleCanSee("user_preferences", localStorage.getItem("cordite_role") ?? "")) {
      prefsRef.current = readFallback();
      setPrefs(prefsRef.current);
      setLoading(false);
      return;
    }
    if (!loadRef.current) {
      loadRef.current = (async () => {
        const rows = (await directus.request(
          readItems("user_preferences", { fields: ["id", "data"], limit: 1 } as any),
        )) as any[];
        if (rows && rows.length > 0) {
          rowIdRef.current = rows[0].id;
          return (rows[0].data ?? {}) as PrefsData;
        }
        // First visit — create the row so subsequent setPref calls can PATCH.
        const me: any = await directus.request(readMe({ fields: ["id"] as any })).catch(() => null);
        const created = (await directus.request(
          createItem("user_preferences", (me?.id ? { user: me.id, data: {} } : { data: {} }) as any),
        )) as any;
        rowIdRef.current = created?.id ?? null;
        return {} as PrefsData;
      })();
    }
    // Deliberately no cancelled-on-unmount guard. The result is applied
    // whenever it lands; dropping it would leave the app running on empty
    // preferences with nothing left to retry.
    loadRef.current
      .then((data) => {
        prefsRef.current = data;
        setPrefs(data);
        if (Array.isArray(data?.favorites)) {
          syncFavoritesFromServer(data.favorites);
        } else {
          const localFavs = loadFavorites();
          if (localFavs.length > 0) {
            setPref("favorites", localFavs);
          }
        }
      })
      .catch(() => {
        // Collection not migrated yet, or permissions not applied — fall back
        // to localStorage so the page degrades gracefully. Clear the shared
        // promise so a later mount can try again rather than inheriting the
        // failure for the rest of the session.
        loadRef.current = null;
        prefsRef.current = readFallback();
        setPrefs(prefsRef.current);
      })
      .finally(() => setLoading(false));
  }, [authed]);

  // Connect favorites persistence to user_preferences
  useEffect(() => {
    if (!authed) {
      registerFavoritesPersister(null);
      clearFavoritesCache();
      return;
    }
    registerFavoritesPersister((favs) => {
      setPref("favorites", favs);
    });
    return () => {
      registerFavoritesPersister(null);
    };
  }, [authed, setPref]);

  // Logging out clears the token; drop the cached blob so the next account does
  // not inherit it, and allow the next login to load afresh.
  useEffect(() => {
    if (authed) return;
    loadRef.current = null;
    rowIdRef.current = null;
    prefsRef.current = {};
    setPrefs({});
    clearFavoritesCache();
  }, [authed]);

  return (
    <PreferencesContext.Provider value={{ prefs, setPref, loading }}>
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences(): PrefsContextValue {
  const ctx = useContext(PreferencesContext);
  if (!ctx) {
    throw new Error("usePreferences must be used within a PreferencesProvider");
  }
  return ctx;
}
